/**
 * The dimetric view — a look test (ADR-0047, ADR-0054).
 *
 * The same world, seen from an angle: the hex grid squashed 2:1, land standing on
 * little cliffs above the water, hills and mountains drawn as terraced stacks, trees
 * as clusters, cities as white blocks, in the flat-shaded low-poly style Mark chose
 * from his "Iron Empire" boards. Two to four tones per shape, lit from the left.
 *
 * It reads only the scene (what the viewer believes is where) and the map's terrain,
 * exactly like the top-down renderer — the fog rules are the scene's, so the two views
 * cannot disagree about what is known. Painter's algorithm: rows are drawn from the
 * back of the screen to the front, and everything on a tile is drawn with its tile,
 * so whatever stands nearer the viewer covers what stands behind it.
 *
 * Canvas 2D on purpose for the test: no new dependency, and it answers the two
 * questions the test is for — does Mark like the look, and is it fast enough — before
 * anything is committed to WebGL.
 */

import type { Content } from '../../content/schema/index.js';
import { hexCorners } from '../core/hex/index.js';
import { neighborsByDirection, tileIndex } from '../core/state/grid.js';
import { VIS_EXPLORED, VIS_UNKNOWN, type TileGrid } from '../core/state/types.js';
import { tileAtScreen, tileCentre, type Camera } from './camera.js';
import { EDGE_CORNERS, frontierPath, parseHex, riverBend, riverNode, shade, tileNoise, toCss, type Rgb } from './cartography.js';
import { drawResourceIcon } from './icons.js';
import { drawImprovementArt } from './improvements.js';
import { drawUnit, drawUnitBadges, type RenderOptions, type RenderStats } from './renderer.js';
import type { Scene } from './scene.js';
import { beginModelFrame, citySprite, facesLeft, unitSprite } from './models/index.js';

/** Vertical squash of the ground plane: 0.5 is true 2:1 dimetric. */
export const SQUASH = 0.5;

const CREAM = parseHex('#f3efe4');
const NIGHT = parseHex('#16202a');
const INK = '#2f3b46';

/** Terraced hill tiers, outermost first: [top, side]. From the "Hills 2 · Terraced" board. */
const HILL_TIERS: Array<[string, string]> = [
  ['#dfe1bf', '#b4b88c'],
  ['#d4d7b0', '#a9ae80'],
  ['#c8cca1', '#9ea374'],
  ['#bcc193', '#93986a'],
];
/** Terraced mountain tiers, with a snow cap. From the "All terraced" board. */
const MOUNTAIN_TIERS: Array<[string, string]> = [
  ['#dfe1bf', '#b4b88c'],
  ['#d6cfb9', '#a9a48a'],
  ['#cbc3aa', '#9d967c'],
  ['#bfb79c', '#918a70'],
  ['#ffffff', '#c9d0d4'],
];
const TREE = ['#8db596', '#7aa585', '#5f8a6b'];
const JUNGLE = ['#6fa27e', '#5d8f6b', '#477558'];
const WATER_EDGE = '#7fb3c8';
const RIVER_BED = '#5f8fa6';
const SHALLOWS = parseHex('#e4efe9');
const ROAD_BED = '#6b5233';
const ROAD_TOP = '#ecd9a6';
/** Below this tile size the view is a whole-continent overview: no fine texture. */
const OVERVIEW_SIZE = 11;
/** At and above this size, the grid layer draws a faint edge round each tile. */
const GRID_SIZE = 18;
/** At and above this tile size, units with a model are drawn as one (ADR-0075). */
const MODEL_SIZE = 22;

export { EDGE_CORNERS };

/** Screen angle of each hex direction, as in renderer.ts (E, NE, NW, W, SW, SE). */
export const DIRECTION_ANGLES = [0, -60, -120, 180, 120, 60].map((deg) => (deg * Math.PI) / 180);

/** Which tile is under a screen point, on the ground plane. */
export function dimetricTileAt(camera: Camera, map: TileGrid, sx: number, sy: number): { col: number; row: number } | null {
  const planY = (sy - camera.viewportHeight / 2) / SQUASH + camera.viewportHeight / 2;
  return tileAtScreen(camera, map, sx, planY);
}

export function renderDimetric(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  content: Content,
  camera: Camera,
  options: RenderOptions,
): RenderStats {
  const started = performance.now();
  beginModelFrame();
  const map = scene.map;
  const size = camera.tileSize;
  const plan = hexCorners(size);
  // The top of each tile is filled half a pixel oversize. Antialiased edges of exactly
  // abutting hexes leave a hairline of background between them, which reads as a dark
  // grid over the whole map; the overlap closes it for the price of nothing.
  const cover = hexCorners(size + 0.5);
  const vw = camera.viewportWidth;
  const vh = camera.viewportHeight;
  const detail = size >= 14;
  const scratch = new Int32Array(6);

  // The empty edges and unexplored land are warm parchment, under the house-style
  // interface (ADR-0074; Mark chose it over the old navy).
  ctx.fillStyle = '#cdc6b3';
  ctx.fillRect(0, 0, vw, vh);

  const toScreen = (wx: number, wy: number): { x: number; y: number } => ({
    x: wx - camera.x + vw / 2,
    y: (wy - camera.y) * SQUASH + vh / 2,
  });

  // Rows from the back of the screen to the front. Tiles below the bottom edge still
  // matter — a mountain three rows down rises into view — so the range runs past it.
  const rowStep = size * 1.5;
  const colStep = Math.sqrt(3) * size;
  const halfWorld = vh / 2 / SQUASH;
  const minRow = Math.max(0, Math.floor((camera.y - halfWorld) / rowStep) - 1);
  const maxRow = Math.min(map.height - 1, Math.ceil((camera.y + halfWorld) / rowStep) + 4);
  const minCol = Math.floor((camera.x - vw / 2) / colStep) - 1;
  const maxCol = Math.ceil((camera.x + vw / 2) / colStep) + 1;

  let tilesDrawn = 0;
  const hover = options.hover;
  let hoverAt: { x: number; y: number; lift: number } | null = null;
  const pathTiles = new Set(options.path ?? []);
  const deferredLabels: Array<() => void> = [];

  for (let row = minRow; row <= maxRow; row++) {
    for (let rawCol = minCol; rawCol <= maxCol; rawCol++) {
      const col = ((rawCol % map.width) + map.width) % map.width;
      const index = tileIndex(map, col, row);
      if (index === -1) continue;
      const level = scene.seen(index);
      if (level === VIS_UNKNOWN) continue;

      const centre = tileCentre(rawCol, row, size);
      const base = toScreen(centre.x, centre.y);
      const terrain = content.terrain.get(map.terrain[index]!);
      const feature = content.features.get(map.feature[index]!);
      const dim = level === VIS_EXPLORED ? 0.45 : 0;
      /** A style colour, faded towards night for remembered ground. */
      const c = (colour: string | Rgb, darken = 0): string => {
        let rgb = typeof colour === 'string' ? parseHex(colour) : colour;
        if (darken !== 0) rgb = shade(rgb, -darken);
        if (dim > 0) rgb = mix(rgb, NIGHT, dim);
        return toCss(rgb);
      };

      // ── the ground: a hexagonal slab, land standing above the water ────────────
      const lift = terrain.water ? 0 : landLift(map, index, size);
      const top = base.y - lift;
      const ground = groundColour(terrain.colour, terrain.water, index);
      if (lift > 0) {
        // The two faces that look towards the viewer: south-west lit, south-east in shade.
        face(ctx, base.x, base.y, plan, 2, 3, lift, c(ground, 0.16));
        face(ctx, base.x, base.y, plan, 1, 2, lift, c(ground, 0.32));
      }
      hexPath(ctx, base.x, top, cover);
      ctx.fillStyle = c(ground);
      ctx.fill();
      if (options.showGrid && size >= GRID_SIZE) {
        hexPath(ctx, base.x, top, plan);
        ctx.strokeStyle = 'rgba(28, 22, 12, 0.07)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      if (terrain.water) shore(ctx, map, scene, content, index, base.x, top, plan, ground, size, scratch, c);
      tilesDrawn++;

      // ── what lies flat on the ground: borders, fields, rivers, roads ───────────
      if (options.showBorders) {
        const owner = scene.ownerColour(index);
        if (owner) {
          hexPath(ctx, base.x, top, plan);
          ctx.fillStyle = owner + '30';
          ctx.fill();
          // The frontier itself, so a border that culture moves can be seen to move.
          neighborsByDirection(map, index, scratch);
          const edge = frontierPath(plan, scene.ownerColour, (i) => scene.seen(i) !== VIS_UNKNOWN, owner, scratch);
          if (edge) {
            onGround(ctx, base.x, top, () => {
              ctx.strokeStyle = c(owner, 0.25);
              ctx.lineWidth = Math.max(1.5, size * 0.07);
              ctx.lineCap = 'round';
              ctx.stroke(edge);
            });
          }
        }
      }
      onGround(ctx, base.x, top, () => {
        if (detail) {
          const improvement = scene.improvement(index);
          if (improvement) {
            // Fields and mines, laid flat and softened: on the boards the ground is plain,
            // and the improvements should read as texture, not as a pattern on every tile.
            ctx.globalAlpha = 0.55;
            drawImprovementArt(ctx, content.improvements.get(improvement).art, 0, 0, size, index, plan);
            ctx.globalAlpha = 1;
          }
        }
        if (options.showRivers && map.rivers[index] !== 0 && !terrain.water) {
          drawRiver(ctx, map, index, map.rivers[index]!, size, scratch, c);
        }
      });
      // Roads are laid in plan but stroked on the screen, so they keep their width at every
      // angle — squashed with the ground, a north–south road all but vanished.
      if (detail) drawRoad(ctx, map, scene, index, size, scratch, c, base.x, top);
      if (options.reachable?.has(index)) {
        hexPath(ctx, base.x, top, plan);
        ctx.fillStyle = 'rgba(255,255,255,0.22)';
        ctx.fill();
      }
      if (options.workedBy !== null && map.workedBy[index] === options.workedBy && detail) {
        ctx.beginPath();
        ctx.ellipse(base.x, top, size * 0.2, size * 0.2 * SQUASH, 0, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = Math.max(1, size * 0.05);
        ctx.stroke();
      }

      // ── what stands on it: relief, trees ───────────────────────────────────────
      if (terrain.key === 'hills') terraces(ctx, base.x, top, size, HILL_TIERS, 0.62, 0.1, c);
      else if (terrain.key === 'mountains') terraces(ctx, base.x, top, size, MOUNTAIN_TIERS, 0.72, 0.17, c);
      if ((feature.key === 'forest' || feature.key === 'rainforest') && size < OVERVIEW_SIZE) {
        // Zoomed right out, a clump of trees is a speckle; a green tint reads as woodland.
        hexPath(ctx, base.x, top, plan);
        ctx.fillStyle = c(feature.key === 'rainforest' ? JUNGLE[1]! : TREE[1]!);
        ctx.globalAlpha = 0.55;
        ctx.fill();
        ctx.globalAlpha = 1;
      } else if (feature.key === 'forest' || feature.key === 'rainforest') {
        trees(ctx, base.x, top, size, index, feature.key === 'rainforest' ? JUNGLE : TREE, c);
      } else if (feature.key === 'marsh' && detail) {
        reeds(ctx, base.x, top, size, index, c);
      } else if (feature.key === 'oasis') {
        ctx.beginPath();
        ctx.ellipse(base.x, top, size * 0.28, size * 0.28 * SQUASH, 0, 0, Math.PI * 2);
        ctx.fillStyle = c('#9cc3d3');
        ctx.fill();
        trees(ctx, base.x + size * 0.25, top, size * 0.6, index, TREE, c);
      }

      // ── resources ──────────────────────────────────────────────────────────────
      if (options.showResources && size >= 18) {
        const resource = content.resources.get(map.resource[index]!);
        if (resource.key !== 'none' && options.resourceSeen?.(resource.id) !== false) {
          drawResourceIcon(ctx, resource.icon, base.x + size * 0.32, top - size * 0.12, size * 0.8, resource.colour, terrain.water);
        }
      }

      // ── cities ─────────────────────────────────────────────────────────────────
      const city = scene.city(index);
      if (city) {
        // A 3D model of the town where the tile is big enough (ADR-0076); blocks otherwise.
        const model =
          size >= MODEL_SIZE
            ? citySprite({ population: city.population, era: city.era, buildings: city.buildings, capital: city.capital, seed: index }, city.colour, size)
            : null;
        if (model) {
          const k = size / model.tile;
          const w = (model.image.width / model.ratio) * k;
          const h = (model.image.height / model.ratio) * k;
          ctx.drawImage(model.image, base.x - (model.anchorX / model.ratio) * k, top - (model.anchorY / model.ratio) * k, w, h);
        } else {
          town(ctx, base.x, top, size, city.population, city.colour, index, c);
        }
        if (size >= 16) {
          const label = `${city.name} ${city.population}`;
          const idle = city.id !== null && options.idleCities?.has(city.id);
          deferredLabels.push(() => cityLabel(ctx, base.x, top + size * 0.45, size, label, idle === true));
        }
      }

      // ── units ──────────────────────────────────────────────────────────────────
      const stack = scene.stackAt(index);
      if (stack) {
        const shown = stack.units.find((u) => u.id === options.selectedUnit) ?? stack.units[0]!;
        const ux = base.x + (city ? size * 0.38 : 0);
        const uy = top - size * (city ? 0.32 : 0.28);
        // A 3D model where there is one and the tile is big enough to show it (ADR-0075);
        // otherwise, and in the flat view, the civ-colour disc.
        const sprite = size >= MODEL_SIZE ? unitSprite(shown.key, shown.colour, facesLeft(shown.id, col + (row & 1) * 0.5), size) : null;
        // In a city the garrison stands smaller, at the front-right corner, so the town shows.
        const gx = base.x + (city ? size * 0.55 : 0);
        const gy = top + size * (city ? 0.22 : 0.04);
        const shrink = city ? 0.68 : 1;
        ctx.beginPath();
        ctx.ellipse(sprite ? gx : ux, sprite ? gy : top + size * 0.04, size * 0.3, size * 0.3 * SQUASH, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(20, 28, 36, 0.28)';
        ctx.fill();
        if (sprite) {
          ctx.beginPath();
          ctx.ellipse(gx, gy, size * 0.42 * shrink, size * 0.42 * shrink * SQUASH, 0, 0, Math.PI * 2);
          ctx.strokeStyle = shown.colour;
          ctx.lineWidth = Math.max(1.5, size * 0.06);
          ctx.stroke();
          const w = sprite.image.width / sprite.ratio;
          const h = sprite.image.height / sprite.ratio;
          // The cache holds a few sizes; scale the nearest to this exact tile size.
          const k = (size / sprite.tile) * shrink;
          ctx.drawImage(sprite.image, gx - (sprite.anchorX / sprite.ratio) * k, gy - (sprite.anchorY / sprite.ratio) * k, w * k, h * k);
        } else {
          drawUnit(ctx, ux, uy, size * 0.72, shown.glyph, shown.colour);
        }
        if (detail) drawUnitBadges(ctx, ux, uy, size * 0.72, shown, stack.units.length);
        if (options.selectedUnit === shown.id) {
          ctx.beginPath();
          ctx.ellipse(ux, top + size * 0.04, size * 0.5, size * 0.5 * SQUASH, 0, 0, Math.PI * 2);
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = Math.max(1.5, size * 0.07);
          ctx.stroke();
        }
      }

      if (pathTiles.has(index)) {
        ctx.beginPath();
        ctx.ellipse(base.x, top, size * 0.13, size * 0.13 * SQUASH, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 236, 170, 0.95)';
        ctx.fill();
      }
      if (hover && hover.col === col && hover.row === row) hoverAt = { x: base.x, y: base.y, lift };
    }
  }

  for (const label of deferredLabels) label();

  if (hoverAt) {
    hexPath(ctx, hoverAt.x, hoverAt.y - hoverAt.lift, plan);
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = Math.max(1.5, size * 0.08);
    ctx.stroke();
  }

  return { tilesDrawn, frameMs: performance.now() - started };
}

// ─── shapes ────────────────────────────────────────────────────────────────────

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/**
 * The style's pastel ground: each terrain's own colour washed towards cream, so the
 * palette stays data-driven (terrain.json) while reading as the boards do.
 */
function groundColour(colour: string, water: boolean, index: number): Rgb {
  const wash = mix(parseHex(colour), water ? parseHex('#cfe0e6') : CREAM, water ? 0.5 : 0.5);
  // A whisper of variation, so a plain is not a single flat sheet.
  return shade(wash, (tileNoise(index, 11) - 0.5) * 0.06);
}

/** How high a land tile stands: a low cliff everywhere, higher ground a little higher. */
function landLift(map: TileGrid, index: number, size: number): number {
  const elevation = map.elevation[index]!;
  return size * (0.22 + Math.max(0, Math.min(1, (elevation - 130) / 125)) * 0.26);
}

function hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, plan: Array<{ x: number; y: number }>): void {
  ctx.beginPath();
  ctx.moveTo(x + plan[0]!.x, y + plan[0]!.y * SQUASH);
  for (let i = 1; i < 6; i++) ctx.lineTo(x + plan[i]!.x, y + plan[i]!.y * SQUASH);
  ctx.closePath();
}

/** One side of the slab: the edge between two corners, from the top down to the water. */
function face(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  plan: Array<{ x: number; y: number }>,
  a: number,
  b: number,
  lift: number,
  colour: string,
): void {
  const pa = plan[a]!;
  const pb = plan[b]!;
  ctx.beginPath();
  ctx.moveTo(x + pa.x, y + pa.y * SQUASH - lift);
  ctx.lineTo(x + pb.x, y + pb.y * SQUASH - lift);
  ctx.lineTo(x + pb.x, y + pb.y * SQUASH);
  ctx.lineTo(x + pa.x, y + pa.y * SQUASH);
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.fill();
}

/** Draw something flat in plan coordinates, laid on the ground at (x, y). */
function onGround(ctx: CanvasRenderingContext2D, x: number, y: number, draw: () => void): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, SQUASH);
  draw();
  ctx.restore();
}

/**
 * Terraces: stacked rings, each a darker side and a lighter top, narrowing and drifting
 * a touch to the right as they rise — the "Hills 2 · Terraced" board, drawn per tile.
 */
function terraces(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  tiers: Array<[string, string]>,
  width: number,
  step: number,
  c: (colour: string) => string,
): void {
  const n = tiers.length;
  for (let i = 0; i < n; i++) {
    const [topColour, sideColour] = tiers[i]!;
    const shrink = 1 - i / (n + 0.6);
    const rx = size * width * shrink;
    const ry = rx * SQUASH;
    const cx = x + size * 0.04 * i;
    const rise = size * step;
    const cy = y - i * rise;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = c(sideColour);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(cx, cy - rise, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = c(topColour);
    ctx.fill();
  }
}

/** A clump of round trees, back ones first, each with a shaded underside. */
function trees(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  index: number,
  palette: string[],
  c: (colour: string) => string,
): void {
  const spots: Array<{ x: number; y: number; r: number }> = [];
  const count = 4;
  for (let i = 0; i < count; i++) {
    const a = tileNoise(index, 40 + i) * Math.PI * 2;
    const d = size * (0.18 + tileNoise(index, 50 + i) * 0.32);
    spots.push({ x: x + Math.cos(a) * d, y: y + Math.sin(a) * d * SQUASH, r: size * (0.14 + tileNoise(index, 60 + i) * 0.06) });
  }
  spots.sort((p, q) => p.y - q.y);
  for (const s of spots) {
    ctx.beginPath();
    ctx.ellipse(s.x, s.y + s.r * 0.15, s.r * 0.9, s.r * 0.45, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(30, 40, 30, 0.18)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(s.x, s.y - s.r * 0.7, s.r, 0, Math.PI * 2);
    ctx.fillStyle = c(palette[2]!);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(s.x - s.r * 0.12, s.y - s.r * 0.82, s.r * 0.86, 0, Math.PI * 2);
    ctx.fillStyle = c(palette[0]!);
    ctx.fill();
  }
}

function reeds(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, index: number, c: (colour: string) => string): void {
  ctx.strokeStyle = c('#7f9a72');
  ctx.lineWidth = Math.max(1, size * 0.04);
  for (let i = 0; i < 5; i++) {
    const px = x + (tileNoise(index, 70 + i) - 0.5) * size;
    const py = y + (tileNoise(index, 80 + i) - 0.5) * size * SQUASH;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + size * 0.04, py - size * 0.18);
    ctx.stroke();
  }
}

type Shade = (colour: string | Rgb, darken?: number) => string;

/**
 * A river, in plan coordinates (the tile's top at the origin): a curved spoke from a
 * wandering junction to the middle of each edge it flows through, the same construction
 * as the top-down map (riverBend) so two tiles' halves meet in one continuous curve.
 *
 * Stroked tile by tile, so the ends are butt-capped: a round cap would poke past the
 * edge and show as a blob where the next tile's half begins. The junction gets its own
 * dot instead. Width grows as the river runs down towards the sea.
 */
function drawRiver(
  ctx: CanvasRenderingContext2D,
  map: TileGrid,
  index: number,
  mask: number,
  size: number,
  scratch: Int32Array,
  c: Shade,
): void {
  const reach = (Math.sqrt(3) / 2) * size;
  const node = riverNode(index, size);
  neighborsByDirection(map, index, scratch);
  const path = new Path2D();
  for (let d = 0; d < 6; d++) {
    if ((mask & (1 << d)) === 0) continue;
    const angle = DIRECTION_ANGLES[d]!;
    const mouth = { x: Math.cos(angle) * reach, y: Math.sin(angle) * reach };
    const bend = riverBend(index, scratch[d]!, node, mouth, size * 0.26);
    path.moveTo(node.x, node.y);
    path.quadraticCurveTo(bend.x, bend.y, mouth.x, mouth.y);
  }
  const water = Math.max(1.5, size * (0.08 + (1 - map.elevation[index]! / 255) * 0.1));
  strokeTwice(ctx, path, node, c(RIVER_BED), water + Math.max(1, size * 0.04), c(WATER_EDGE), water);
}

/**
 * Roads: a gently curved spoke to each neighbour that is road too, laid out in plan
 * coordinates and then mapped onto the screen at (x, y), so the stroke itself is not
 * squashed. A dark bed under a pale surface reads on every ground colour (ADR-0058).
 */
function drawRoad(
  ctx: CanvasRenderingContext2D,
  map: TileGrid,
  scene: Scene,
  index: number,
  size: number,
  scratch: Int32Array,
  c: Shade,
  x: number,
  y: number,
): void {
  if (!scene.road(index)) return;
  const city = scene.city(index) !== null;
  // Every city stands on a road and is drawn as the junction, at its centre.
  const node = city ? { x: 0, y: 0 } : riverNode(index, size * 0.55, 96);
  neighborsByDirection(map, index, scratch);
  const reach = (Math.sqrt(3) / 2) * size;
  const path = new Path2D();
  let links = 0;
  for (let d = 0; d < 6; d++) {
    const other = scratch[d]!;
    if (other === -1 || scene.seen(other) === VIS_UNKNOWN || !scene.road(other)) continue;
    const angle = DIRECTION_ANGLES[d]!;
    const mouth = { x: Math.cos(angle) * reach, y: Math.sin(angle) * reach };
    const bend = riverBend(index, other, node, mouth, size * 0.1, 98);
    path.moveTo(node.x, node.y);
    path.quadraticCurveTo(bend.x, bend.y, mouth.x, mouth.y);
    links++;
  }
  if (links === 0) {
    if (city) return;
    // A stretch of road not yet joined to anything: a short track through the tile.
    const a = tileNoise(index, 99) * Math.PI;
    const half = size * 0.42;
    path.moveTo(node.x - Math.cos(a) * half, node.y - Math.sin(a) * half);
    path.lineTo(node.x + Math.cos(a) * half, node.y + Math.sin(a) * half);
  }
  const onScreen = new Path2D();
  onScreen.addPath(path, new DOMMatrix([1, 0, 0, SQUASH, x, y]));
  strokeTwice(
    ctx,
    onScreen,
    { x: x + node.x, y: y + node.y * SQUASH },
    c(ROAD_BED),
    Math.max(3, size * 0.15),
    c(ROAD_TOP),
    Math.max(1.4, size * 0.075),
  );
}

/** A cased line: bed then surface, butt-capped, with a round junction at the node. */
function strokeTwice(
  ctx: CanvasRenderingContext2D,
  path: Path2D,
  node: { x: number; y: number },
  under: string,
  underWidth: number,
  over: string,
  overWidth: number,
): void {
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  for (const [colour, width] of [
    [under, underWidth],
    [over, overWidth],
  ] as const) {
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.stroke(path);
    ctx.beginPath();
    ctx.arc(node.x, node.y, width / 2, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.fill();
  }
}

/**
 * Where water meets land: paler shallows fading out from each shore edge, and a thin
 * line of surf at the foot of the cliff. Drawn on the water tile, at water level and in
 * plan coordinates, so a cliff in a nearer row covers it exactly as it would the sea.
 *
 * Each shore edge gets a wedge from its two corners towards the centre, shaded from pale
 * at the edge to the water's own colour inside. Neighbouring wedges share a side and
 * agree along it, so a bay of several shore edges shades as one smooth curve.
 */
function shore(
  ctx: CanvasRenderingContext2D,
  map: TileGrid,
  scene: Scene,
  content: Content,
  index: number,
  x: number,
  y: number,
  plan: Array<{ x: number; y: number }>,
  ground: Rgb,
  size: number,
  scratch: Int32Array,
  c: Shade,
): void {
  neighborsByDirection(map, index, scratch);
  let shores = 0;
  for (let d = 0; d < 6; d++) {
    const other = scratch[d]!;
    if (other === -1 || scene.seen(other) === VIS_UNKNOWN) continue;
    if (content.terrain.get(map.terrain[other]!).water) continue;
    shores |= 1 << d;
  }
  if (shores === 0) return;

  const pale = c(mix(ground, SHALLOWS, 0.45));
  const deep = c(ground);
  const depth = 0.55;
  onGround(ctx, x, y, () => {
    for (let d = 0; d < 6; d++) {
      if ((shores & (1 << d)) === 0) continue;
      const [a, b] = EDGE_CORNERS[d]!;
      const pa = plan[a]!;
      const pb = plan[b]!;
      const mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 };
      const gradient = ctx.createLinearGradient(mid.x, mid.y, mid.x * (1 - depth), mid.y * (1 - depth));
      gradient.addColorStop(0, pale);
      gradient.addColorStop(1, deep);
      ctx.beginPath();
      ctx.moveTo(pa.x, pa.y);
      ctx.lineTo(pb.x, pb.y);
      ctx.lineTo(pb.x * (1 - depth), pb.y * (1 - depth));
      ctx.lineTo(pa.x * (1 - depth), pa.y * (1 - depth));
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    }
    if (size < OVERVIEW_SIZE) return;

    // Surf: one soft line just off the cliff foot.
    const inset = 0.94;
    ctx.beginPath();
    for (let d = 0; d < 6; d++) {
      if ((shores & (1 << d)) === 0) continue;
      const [a, b] = EDGE_CORNERS[d]!;
      ctx.moveTo(plan[a]!.x * inset, plan[a]!.y * inset);
      ctx.lineTo(plan[b]!.x * inset, plan[b]!.y * inset);
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = c('#ffffff');
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = Math.max(1, size * 0.05);
    ctx.stroke();
    ctx.globalAlpha = 1;
  });
}

/**
 * A town: white blocks, more of them as it grows, the tallest roofed in its owner's
 * colour — the boards' white blocky buildings, with the one splash of colour that says
 * whose it is.
 */
function town(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  population: number,
  colour: string,
  index: number,
  c: (colour: string | Rgb, darken?: number) => string,
): void {
  const count = Math.min(7, 2 + Math.floor(population / 2));
  const blocks: Array<{ x: number; y: number; w: number; h: number; owner: boolean }> = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + tileNoise(index, 90) * 2;
    const d = i === 0 ? 0 : size * (0.22 + tileNoise(index, 91 + i) * 0.2);
    blocks.push({
      x: x + Math.cos(a) * d,
      y: y + Math.sin(a) * d * SQUASH,
      w: size * (i === 0 ? 0.24 : 0.16 + tileNoise(index, 100 + i) * 0.06),
      h: size * (i === 0 ? 0.42 + Math.min(population, 16) * 0.012 : 0.17 + tileNoise(index, 110 + i) * 0.16),
      owner: i === 0,
    });
  }
  blocks.sort((p, q) => p.y - q.y);
  const owner = parseHex(colour);
  for (const b of blocks) {
    const topColour = b.owner ? owner : parseHex('#ffffff');
    // Left face, right face, then the lid — an isometric box, as on the boards.
    ctx.beginPath();
    ctx.moveTo(b.x - b.w, b.y);
    ctx.lineTo(b.x, b.y + b.w * SQUASH);
    ctx.lineTo(b.x, b.y + b.w * SQUASH - b.h);
    ctx.lineTo(b.x - b.w, b.y - b.h);
    ctx.closePath();
    ctx.fillStyle = c(b.owner ? shade(owner, -0.12) : parseHex('#e4dfd2'));
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(b.x, b.y + b.w * SQUASH);
    ctx.lineTo(b.x + b.w, b.y);
    ctx.lineTo(b.x + b.w, b.y - b.h);
    ctx.lineTo(b.x, b.y + b.w * SQUASH - b.h);
    ctx.closePath();
    ctx.fillStyle = c(b.owner ? shade(owner, -0.26) : parseHex('#cfc9ba'));
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(b.x - b.w, b.y - b.h);
    ctx.lineTo(b.x, b.y - b.h - b.w * SQUASH);
    ctx.lineTo(b.x + b.w, b.y - b.h);
    ctx.lineTo(b.x, b.y - b.h + b.w * SQUASH);
    ctx.closePath();
    ctx.fillStyle = c(topColour);
    ctx.fill();
  }
}

function cityLabel(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, label: string, idle: boolean): void {
  ctx.font = `600 ${Math.max(9, Math.min(15, Math.round(size * 0.34)))}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const w = ctx.measureText(label).width + size * 0.4;
  const h = Math.max(12, Math.min(20, size * 0.48));
  ctx.fillStyle = 'rgba(243, 239, 228, 0.92)';
  ctx.beginPath();
  ctx.roundRect(x - w / 2, y, w, h, h / 2);
  ctx.fill();
  if (idle) {
    ctx.strokeStyle = '#e4572e';
    ctx.lineWidth = Math.max(1.5, size * 0.06);
    ctx.stroke();
  }
  ctx.fillStyle = INK;
  ctx.fillText(label, x, y + h * 0.14);
}
